import React from 'react';
import { useSpc } from '../context/SpcContext';
import {
  Footprints, Activity, Zap, Medal, Trophy, Target, Flame, Star, Sparkles, Flag, Mountain, Waves, Timer,
  HeartHandshake, Megaphone, Users, UserPlus, Rocket, TrendingUp, ShieldCheck, BadgeCheck, Award, Crown,
  Sunrise, Moon, Bike, Dumbbell, Compass, MapPin, Gift, PartyPopper, Snowflake, Sun, CloudRain, Globe, Cake,
  BookOpen, Brain, RotateCcw, CalendarCheck, CalendarDays, Gem, Shield, Camera, MessageCircle, Infinity as InfinityIcon,
  Droplet, TreePine, Backpack, Milestone, Crosshair, Repeat, Swords, HelpCircle, Coins, PiggyBank, Wallet,
  Heart, Smile, Shirt, Tent, GraduationCap, Landmark, Layers, Route, Umbrella
} from 'lucide-react';

const ACH_ICONS = {
  first_session: Footprints, sessions_5: Activity, sessions_10: Zap, sessions_25: Flame, sessions_50: Medal,
  sessions_75: BadgeCheck, sessions_100: Trophy, sessions_150: Star, sessions_200: Gem, sessions_300: Layers,
  sessions_365: CalendarDays, sessions_500: Award, sessions_750: ShieldCheck, sessions_1000: Crown,
  challenge_1: Target, challenge_3: Crosshair, challenge_6: Flame, challenge_12: CalendarCheck, challenge_24: Rocket,
  challenge_36: Mountain, challenge_48: Swords, challenge_60: Trophy,
  referral_1: UserPlus, referral_3: Users, referral_5: Rocket, referral_10: TrendingUp, referral_15: Megaphone,
  referral_20: Landmark, referral_25: Gem,
  points_500: Coins, points_1000: PiggyBank, points_2500: Wallet, points_5000: Gem, points_10000: Sparkles,
  member_1y: Star, member_3y: Shield, member_5y: Sparkles, member_10y: Landmark,
  run_5k: Footprints, run_10k: Route, run_half: Flag, marathon: Medal, ultra: Mountain, trail_run: TreePine,
  night_run: Moon, sunrise_run: Sunrise, pb_run: TrendingUp,
  cycle_first: Bike, cycle_50k: Route, cycle_century: Flag, cycle_climb: Mountain, cycle_bikepack: Backpack,
  cycle_commute: MapPin,
  swim_first: Droplet, swim_1k: Waves, swim_open_water: Compass, swim_mile: Flag, swim_dawn: Sunrise, swim_ice: Snowflake,
  tri_sprint: Zap, tri_olympic: Medal, tri_half: ShieldCheck, tri_full: Crown, tri_relay: Users, duathlon: Repeat,
  sc_first: Dumbbell, sc_10: Flame, sc_25: ShieldCheck, sc_pb: TrendingUp, mobility_streak: Repeat,
  mindset_streak: Brain, mindset_month: CalendarCheck, goal_setter: Target, goal_smasher: Crosshair,
  comeback: RotateCcw, consistency_30: Repeat, consistency_100: InfinityIcon, streak_365: Shield,
  race_1: Flag, race_3: MapPin, race_5: Route, race_10: Milestone, podium: Medal, win: Trophy, club_record: Crown,
  social_1: Smile, social_5: Users, social_10: PartyPopper, volunteer: HeartHandshake, pacer: Timer,
  support_crew: Megaphone, photo_contrib: Camera, post_5: MessageCircle, kindness: Heart,
  coached_start: GraduationCap, coached_1y: TrendingUp, zone4: ShieldCheck, coach_cert: BadgeCheck,
  kit_proud: Shirt, camp_alumni: Tent, spc_passport: BadgeCheck, spc_ambassador: Megaphone, night_champion: Moon,
  first_100_member: Star, leaderboard_top10: TrendingUp, leaderboard_1: Trophy, referrals_stripe: Users, spc_wisdom: BookOpen,
  new_year: PartyPopper, santa_run: Gift, summer_solstice: Sun, winter_warrior: Snowflake, storm_runner: CloudRain,
  double_header: Layers, early_bird: Sunrise, rain_warrior: Umbrella, globe_trotter: Globe, anniversary_event: Cake,
  founders_pick: Award, mystery: HelpCircle, club_champion: Crown
};

const CATEGORY_COLORS = {
  Sessions: '#e1e20a', Challenges: '#ff9f43', Referrals: '#f472b6', Points: '#ffd23f', Membership: '#5ac8fa',
  Running: '#4ade80', Cycling: '#14b8a6', Swimming: '#38bdf8', Triathlon: '#a78bfa', Strength: '#f87171',
  Mindset: '#c084fc', Racing: '#fb7185', Community: '#fb923c', Coaching: '#22d3ee', SPC: '#facc15', Special: '#ffb020'
};
export function catColor(category) { return CATEGORY_COLORS[category] || 'var(--spc-yellow)'; }

export function AchIcon({ a, size = 26 }) {
  const I = ACH_ICONS[a.id] || Award;
  return <I size={size} strokeWidth={1.9} />;
}

export function AchievementCard({ a }) {
  const { isAchievementUnlocked, achievementEarnedDate, achievementMetricValue, unlockAchievement } = useSpc();
  const unlocked = isAchievementUnlocked(a.id);
  const current = achievementMetricValue(a);
  const pct = Math.min(100, Math.round((current / a.target) * 100));
  const claimable = !unlocked && a.metric !== 'manual' && current >= a.target;
  let progressText = 'Unlocked';
  if (!unlocked) progressText = a.metric === 'manual' ? 'Awarded by SPC' : `${Math.min(current, a.target)} / ${a.target}`;
  return (
    <div className={`achievement ${unlocked ? 'unlocked' : 'locked'} ${claimable ? 'claimable' : ''}`} data-testid={`ach-card-${a.id}`}>
      {unlocked && (
        <>
          <span className="ach-sparkle s1" aria-hidden="true" />
          <span className="ach-sparkle s2" aria-hidden="true" />
          <span className="ach-sparkle s3" aria-hidden="true" />
          <span className="ach-sparkle s4" aria-hidden="true" />
        </>
      )}
      <div className="achievement-icon" style={{ '--cat-color': catColor(a.category) }}><AchIcon a={a} /></div>
      <span className={`pill ${unlocked ? 'cat-tinted' : ''}`} style={unlocked ? { '--cat-color': catColor(a.category) } : undefined}>{a.category}</span>
      <h4 style={{ marginTop: 10 }}>{a.title}</h4>
      <p>{a.description}</p>
      {unlocked
        ? <div className="achievement-earned">UNLOCKED · {achievementEarnedDate(a.id)}</div>
        : claimable
          ? <button className="btn achievement-claim" data-testid={`ach-claim-${a.id}`} onClick={() => unlockAchievement(a.id)}>Claim{a.points ? ` +${a.points}` : ''}</button>
          : (<>
              <div className="achievement-lock">{progressText}</div>
              <div className="progress" style={{ marginTop: 8 }}><span style={{ width: `${pct}%` }} /></div>
            </>)}
    </div>
  );
}

export function AchievementCabinet() {
  const { state, isAchievementUnlocked } = useSpc();
  const [filter, setFilter] = React.useState('All');
  const cat = state.achievementCatalogue;
  const categories = ['All', ...Array.from(new Set(cat.map(a => a.category)))];
  const items = cat.filter(a => filter === 'All' || a.category === filter);
  const unlockedCount = cat.filter(a => isAchievementUnlocked(a.id)).length;
  return (
    <>
      <div className="grid">
        <div className="card span-4"><div className="stat"><strong>{unlockedCount}</strong><span>unlocked</span></div></div>
        <div className="card span-4"><div className="stat"><strong data-testid="cabinet-total-count">{cat.length}</strong><span>total achievements</span></div></div>
        <div className="card span-4"><div className="stat"><strong>{Math.round(unlockedCount / cat.length * 100)}%</strong><span>cabinet complete</span></div></div>
      </div>
      <div className="achievement-filter">
        {categories.map(c => (
          <button key={c} className={filter === c ? 'active' : ''} data-testid={`ach-filter-${c}`} onClick={() => setFilter(c)}>{c}</button>
        ))}
      </div>
      <div className="achievement-grid">
        {items.map(a => <AchievementCard key={a.id} a={a} />)}
      </div>
    </>
  );
}
